/**
 * Meanbook Note - Sound Synthesis & Notification Module
 */

export function playSynthSound(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    if (type === 'chime') {
      const playNote = (freq, delay, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + delay);
        gain.gain.setValueAtTime(0, now + delay);
        gain.gain.linearRampToValueAtTime(0.25, now + delay + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + delay + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + delay);
        osc.stop(now + delay + duration);
      };
      playNote(523.25, 0, 0.4);    // C5
      playNote(659.25, 0.08, 0.4);  // E5
      playNote(783.99, 0.16, 0.4);  // G5
      playNote(1046.50, 0.24, 0.6); // C6
    } else if (type === 'laser') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(1500, now);
      osc.frequency.exponentialRampToValueAtTime(150, now + 0.45);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.45);
    } else { // 'default' / bell
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, now); // D5
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880.00, now); // A5

      gain.gain.setValueAtTime(0.35, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 1.0);
      osc2.stop(now + 1.0);
    }
  } catch (e) {
    console.error("Synthesizer sound playback failed:", e);
  }
}

export function triggerNotificationSound(forcedType = null) {
  const soundType = forcedType || localStorage.getItem("notiSoundType") || "default";
  if (soundType && soundType.startsWith("custom")) {
    const customData = localStorage.getItem(`globalCustomSound_${soundType}`);
    if (customData) {
      try {
        const audio = new Audio(customData);
        audio.volume = 0.8;
        audio.play().catch(e => {
          console.warn("Audio elements autoplay blocked, trying fallback synthesizer:", e);
          playSynthSound("default");
        });
      } catch (e) {
        console.error("Failed to play custom sound, falling back:", e);
        playSynthSound("default");
      }
    } else {
      playSynthSound("default");
    }
  } else {
    playSynthSound(soundType);
  }
}

export function isNotificationAllowedNow(assignedRoles = []) {
  const isAllowedRole = assignedRoles.includes("Super Admin") || assignedRoles.includes("Packer & Picker");
  if (!isAllowedRole) return false;

  const enabled = localStorage.getItem("notiEnabled") !== "false";
  if (!enabled) return false;

  const now = new Date();
  const currentDay = now.getDay();
  const savedDays = JSON.parse(localStorage.getItem("notiDays") || "[1,2,3,4,5,6,0]");
  if (!savedDays.includes(currentDay)) return false;

  const startTime = localStorage.getItem("notiStartTime") || "08:00";
  const endTime = localStorage.getItem("notiEndTime") || "18:00";
  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;

  const currentMin = now.getHours() * 60 + now.getMinutes();
  if (currentMin < startMin || currentMin > endMin) return false;

  return true;
}

export function createNotification(order) {
  const title = "มีออเดอร์ใหม่รอแพ็ค! 📦";
  const customerName = order.customerName || order.thaiName || "ไม่ระบุชื่อ";
  const items = order.items || "ไม่ระบุรายการสินค้า";

  const options = {
    body: `ลูกค้า: ${customerName}\nสินค้า: ${items}`,
    icon: "icon-512.png",
    badge: "icon-512.png",
    tag: `new-order-${order.id}`,
    renotify: true
  };

  try {
    new Notification(title, options);
  } catch (e) {
    console.error("Local notification display failed, fallback using SW if registered:", e);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(reg => {
        reg.showNotification(title, options);
      });
    }
  }
}

export function sendLocalWebNotification(order) {
  if (!("Notification" in window)) return;

  if (Notification.permission === "granted") {
    createNotification(order);
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then(permission => {
      if (permission === "granted") {
        createNotification(order);
      }
    });
  }
}

export function createCombinedNotification(count) {
  const title = "มีออเดอร์ใหม่รอแพ็ค! 📦";
  const options = {
    body: `มี Order รอแพ็ค ${count} รายการ`,
    icon: "icon-512.png",
    badge: "icon-512.png",
    tag: `combined-orders-${Date.now()}`,
    renotify: true
  };

  try {
    new Notification(title, options);
  } catch (e) {
    console.error("Local notification display failed, fallback using SW if registered:", e);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(reg => {
        reg.showNotification(title, options);
      });
    }
  }
}

export function sendCombinedWebNotification(count) {
  if (!("Notification" in window)) return;

  if (Notification.permission === "granted") {
    createCombinedNotification(count);
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then(permission => {
      if (permission === "granted") {
        createCombinedNotification(count);
      }
    });
  }
}

export function updateAppBadge(count) {
  if ("setAppBadge" in navigator) {
    if (count > 0) {
      navigator.setAppBadge(count).catch(err => console.error("Error setting app badge:", err));
    } else {
      navigator.clearAppBadge().catch(err => console.error("Error clearing app badge:", err));
    }
  }
}

export function populateTimeDropdowns() {
  const startHour = document.getElementById("setting-noti-start-hour");
  const startMin = document.getElementById("setting-noti-start-minute");
  const endHour = document.getElementById("setting-noti-end-hour");
  const endMin = document.getElementById("setting-noti-end-minute");

  if (!startHour || !startMin || !endHour || !endMin) return;
  if (startHour.options.length > 0) return;

  for (let h = 0; h < 24; h++) {
    const padH = String(h).padStart(2, '0');
    startHour.add(new Option(padH, padH));
    endHour.add(new Option(padH, padH));
  }

  for (let m = 0; m < 60; m += 5) {
    const padM = String(m).padStart(2, '0');
    startMin.add(new Option(padM, padM));
    endMin.add(new Option(padM, padM));
  }
}
